// The wire vocabulary for portable skills, shared by the API routes, the
// Developer MCP registry, and the Spec runtime so none of them can drift.
import { z } from 'zod';

export const SkillAudienceSchema = z.enum(['personal', 'workspace']);
export type SkillAudience = z.infer<typeof SkillAudienceSchema>;

export const SkillSourceSchema = z.enum(['custom', 'official', 'system']);
export type SkillSource = z.infer<typeof SkillSourceSchema>;

export const SkillLifecycleSchema = z.enum(['draft', 'published', 'unpublished', 'deleted']);
export type SkillLifecycle = z.infer<typeof SkillLifecycleSchema>;

/** Where a read or write entered the system, for receipts and audit rows. */
export const SkillSurfaceSchema = z.enum(['web', 'api', 'spec', 'mcp', 'system']);
export type SkillSurface = z.infer<typeof SkillSurfaceSchema>;

/** The portable format carries prose and inert data only; nothing executable. */
export const SkillFileMediaTypeSchema = z.enum(['text/markdown', 'text/plain', 'application/json']);
export type SkillFileMediaType = z.infer<typeof SkillFileMediaTypeSchema>;

// Strict: a symlink target, mode bit, or binary descriptor riding along in a
// fourth key is refused here rather than deeper in the validator.
export const SkillFileInputSchema = z
  .object({
    path: z.string(),
    mediaType: SkillFileMediaTypeSchema,
    content: z.string(),
  })
  .strict();
export type SkillFileInput = z.infer<typeof SkillFileInputSchema>;

export const SkillBundleInputSchema = z
  .object({
    files: z.array(SkillFileInputSchema).readonly(),
  })
  .strict();
export type SkillBundleInput = z.infer<typeof SkillBundleInputSchema>;

/** System skills are addressed by slug, never by locator, so `system` is absent here. */
export const SkillLocatorSchema = z.discriminatedUnion('source', [
  z
    .object({
      source: z.literal('custom'),
      skillId: z.string(),
      revisionId: z.string().optional(),
      view: z.enum(['published', 'draft']).optional(),
    })
    .strict(),
  z
    .object({
      source: z.literal('official'),
      officialSkillId: z.string(),
      releaseId: z.string().optional(),
    })
    .strict(),
]);
export type SkillLocator = z.infer<typeof SkillLocatorSchema>;

/** A way to ASK for a skill. Never returned: a skill is identified by SkillLocator only. */
export const SkillReadLocatorSchema = z.discriminatedUnion('source', [
  ...SkillLocatorSchema.options,
  z.object({ source: z.literal('name'), name: z.string().min(1).max(64) }).strict(),
]);
export type SkillReadLocator = z.infer<typeof SkillReadLocatorSchema>;

/** What every skill write returns. Never carries skill content. */
export const SkillWriteReceiptSchema = z
  .object({
    skillId: z.string().nullable(),
    installationId: z.string().nullable(),
    draftVersion: z.number().int().nullable(),
    publishedRevisionId: z.string().nullable(),
    lifecycle: SkillLifecycleSchema.nullable(),
    changed: z.boolean(),
  })
  .strict();
export type SkillWriteReceipt = z.infer<typeof SkillWriteReceiptSchema>;

/** Slugs the system skills own; no custom or official skill may claim them. */
export const SYSTEM_SKILL_RESERVED_SLUGS = [
  'create-skill',
  'improve-skill',
  'add-knowledge',
  'set-up-score',
] as const;
export type SystemSkillSlug = (typeof SYSTEM_SKILL_RESERVED_SLUGS)[number];

/** What a member asks the hidden authoring workflows to do; web, API and Spec share it. */
export const SkillWorkflowInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('create_skill') }).strict(),
  z.object({ kind: z.literal('improve_skill'), targetSkillId: z.string() }).strict(),
  z.object({ kind: z.literal('add_knowledge') }).strict(),
  z.object({ kind: z.literal('set_up_score') }).strict(),
]);
export type SkillWorkflowInput = z.infer<typeof SkillWorkflowInputSchema>;

/** The current version of each system workflow; a bound session pins the one it started on. */
export const SYSTEM_SKILL_WORKFLOW_VERSIONS = {
  create_skill: 1,
  improve_skill: 1,
  add_knowledge: 1,
  set_up_score: 1,
} as const;
export type SystemSkillWorkflowKey = keyof typeof SYSTEM_SKILL_WORKFLOW_VERSIONS;

/** The binding between a Spec conversation and one system workflow. Never carries content. */
export const SkillAuthoringSessionSummarySchema = z
  .object({
    conversationId: z.string(),
    workflow: SkillWorkflowInputSchema,
    workflowVersion: z.number().int(),
    targetSkillId: z.string().nullable(),
  })
  .strict();
export type SkillAuthoringSessionSummary = z.infer<typeof SkillAuthoringSessionSummarySchema>;

/** Hard ceiling for paged skill reads; the service, the routes and MCP share it. */
export const SKILL_PAGE_MAX_LIMIT = 200;

/** `q` is ranked in memory per item, so it is bounded; the routes, filters and MCP share it. */
export const SKILL_QUERY_MAX_LENGTH = 500;

/** Every bound the portable format enforces. */
/** Lives here so MCP schemas and the Spec runtime read it without apps/api. */
export const SKILL_FORMAT_LIMITS = {
  nameChars: 64,
  descriptionChars: 512,
  compatibilityChars: 500,
  metadataBytes: 8_192,
  pathChars: 240,
  pathDepth: 5,
  files: 25,
  instructionsBytes: 102_400,
  fileBytes: 102_400,
  bundleBytes: 512_000,
} as const;

// Every rule id the bundle validator can reject with. The validator's error
// type is pinned to this list, so a new rule must be declared here first.
export const SKILL_FORMAT_RULES = [
  'bundle.bytes',
  'bundle.empty',
  'bundle.files',
  'bundle.root_duplicate',
  'bundle.root_missing',
  'bundle.schema',
  'content.control_character',
  'content.lone_surrogate',
  'content.nul_byte',
  'content.shebang',
  'file.bytes',
  'frontmatter.alias',
  'frontmatter.compatibility_invalid',
  'frontmatter.compatibility_length',
  'frontmatter.description_invalid',
  'frontmatter.description_length',
  'frontmatter.description_missing',
  'frontmatter.host_field',
  'frontmatter.license_invalid',
  'frontmatter.metadata_bytes',
  'frontmatter.metadata_invalid',
  'frontmatter.metadata_reserved',
  'frontmatter.missing',
  'frontmatter.multiple_documents',
  'frontmatter.name_invalid',
  'frontmatter.name_length',
  'frontmatter.name_missing',
  'frontmatter.not_a_map',
  'frontmatter.parse_error',
  'frontmatter.unknown_key',
  'frontmatter.unterminated',
  'html.embed',
  'html.event_handler',
  'html.form',
  'html.iframe',
  'html.javascript_url',
  'html.link',
  'html.meta_refresh',
  'html.object',
  'html.script',
  'instructions.bytes',
  'json.host_manifest',
  'json.invalid',
  'media_type.mismatch',
  'path.absolute',
  'path.backslash',
  'path.control_character',
  'path.depth',
  'path.duplicate',
  'path.empty_segment',
  'path.hidden_segment',
  'path.length',
  'path.root_conflict',
  'path.traversal',
  'path.unsupported_extension',
  'reference.missing_file',
  'secret.anthropic_key',
  'secret.aws_access_key',
  'secret.aws_secret_access_key',
  'secret.aws_session_token',
  'secret.bearer_token',
  'secret.edge_api_token',
  'secret.github_token',
  'secret.google_api_key',
  'secret.jwt',
  'secret.oauth_client_secret',
  'secret.oauth_code',
  'secret.oauth_token',
  'secret.openai_key',
  'secret.private_key',
  'secret.slack_token',
  'secret.stripe_key',
  'secret.unknown',
] as const;
export type SkillFormatRule = (typeof SKILL_FORMAT_RULES)[number];

const SKILL_FORMAT_RULE_SET: ReadonlySet<string> = new Set(SKILL_FORMAT_RULES);

/** Narrows a caller-supplied string, used where a detector kind becomes a rule. */
export function isSkillFormatRule(value: string): value is SkillFormatRule {
  return SKILL_FORMAT_RULE_SET.has(value);
}

/** Skill service reasons, distinct from the validator rules above. */
export const SKILL_SERVICE_ERROR_REASONS = [
  'api_key_creator_inactive',
  'authoring_session_already_bound',
  'authoring_session_required',
  'authoring_session_reuse_denied',
  'authoring_session_target_mismatch',
  'authoring_session_workflow_mismatch',
  'bad_bundle',
  'denied',
  'digest_mismatch',
  'draft_not_found',
  'draft_preview_denied',
  'file_not_found',
  'format',
  'idempotency_key',
  'idempotency_key_required',
  'in_progress',
  'installation_not_found',
  'l1_results',
  'loaded_skills_per_run',
  'maintainer_required',
  'missing',
  'not_a_member',
  'not_maintainer',
  'not_populated',
  'official_not_installable',
  'official_read_only',
  'official_release_missing',
  'official_skill_not_found',
  'official_source_not_populated',
  'official_source_unavailable',
  'override_audience',
  'personal_denied',
  'personal_forbidden',
  'pinned_release_unavailable',
  'reserved_slug',
  'revision_and_draft_view',
  'revision_not_found',
  'run_not_found',
  'runtime_block',
  'runtime_block_unavailable',
  'skill_deleted',
  'skill_forbidden',
  'skill_loading_off',
  'skill_name_ambiguous',
  'skill_not_found',
  'skill_writes_off',
  'skills_off',
  'slug_taken',
  'source_down',
  'source_unavailable',
  'stale_draft_version',
  'stale_maintainer',
  'stale_version',
  'transfer_personal',
  'unavailable',
  'update_source',
  'workspace_mismatch',
  'workspace_required',
  'write_failed',
] as const;

// The one allowlist the API projection and the MCP relay both read; anything
// outside it becomes `request_failed`.
export const PUBLIC_SKILL_ERROR_REASONS: ReadonlySet<string> = new Set([
  ...SKILL_SERVICE_ERROR_REASONS,
  ...SKILL_FORMAT_RULES,
]);
